import { pickT } from "@repo/shared";
import type { ProductDetailsResponse, Translated } from "@repo/shared/types";
import { ExternalLink, Info, Loader2 } from "lucide-react";
import { useMemo, useState, useTransition } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  getPortfolioProductDetails,
  getProductDetails,
} from "@/lib/api/flow-instances/portfolio-snapshots";
import { getPublicProductDetails } from "@/lib/api/public-flow/product-details";
import { usePublicSession } from "@/lib/context/public-session-context";
import { useUserLanguage } from "@/lib/i18n/context";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { ComponentStyleType } from "@/lib/types/flow";
import { getRelativeTime } from "@/lib/utils";
import { OverriddenCostValue } from "./cost-override";
import { PortfolioDisplayChart, portfolioDisplaySlices } from "./portfolio-display-chart";
import { T } from "./portfolio-display-widget.i18n";

// Product entry in snapshot
type SnapshotProduct = {
  // Null for an unmatchable holding (QL-304) — kept in the composition at its
  // true weight but with no collection product to reference.
  productId: string | null;
  productName: string;
  isin: string | null;
  amount: number;
  weight: number;
};

// Goal-level portfolio snapshot (supports both model and custom portfolios)
type PortfolioSnapshot = {
  portfolioName?: string; // Only present for model portfolios
  products?: SnapshotProduct[];
  totalAmount: number;
  currency: string;
  timestamp?: string;
};

type PortfolioDisplayWidgetProps = {
  snapshot: PortfolioSnapshot;
  /** QL-741 — the products' weights as a pie chart. Default: false. */
  showChart?: boolean;
  /** QL-741 — the products table. Default: true; shown anyway when the chart is off too. */
  showTable?: boolean;
  /** The tenant's chart palette; null = the default one. */
  chartColors?: string[] | null;
  showPortfolioName?: boolean;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  language?: string;
  /** Advisor surface: the portfolio shown, so the info modal applies the
   * instance's cost overrides (QL-20). The public read uses the session's. */
  portfolioId?: string | null;
};

// Product Info Modal Component
function ProductInfoModal({
  open,
  onClose,
  product,
  isLoading,
  t,
}: {
  open: boolean;
  onClose: () => void;
  product: ProductDetailsResponse | null;
  isLoading: boolean;
  t: Translated<typeof T>;
}) {
  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{product?.name || t.loading}</DialogTitle>
          {product?.isin && (
            <DialogDescription className="font-mono">{product.isin}</DialogDescription>
          )}
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : product ? (
          <div className="space-y-4">
            {/* Description */}
            {product.description && (
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-1">
                  {t.description}
                </div>
                <p className="text-sm">{product.description}</p>
              </div>
            )}

            {/* Suitability */}
            {(product.productType ||
              product.minTimeHorizon ||
              product.minRiskLevel ||
              (product.wrapperTypes?.length ?? 0) > 0) && (
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  {t.suitability}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {product.productType && (
                    <>
                      <span className="text-muted-foreground">{t.productType}:</span>
                      <span>{product.productType.name}</span>
                    </>
                  )}
                  {product.minTimeHorizon && (
                    <>
                      <span className="text-muted-foreground">{t.minTimeHorizon}:</span>
                      <span>{product.minTimeHorizon.label}</span>
                    </>
                  )}
                  {product.minRiskLevel && (
                    <>
                      <span className="text-muted-foreground">{t.minRiskTolerance}:</span>
                      <span>{product.minRiskLevel.label}</span>
                    </>
                  )}
                  {(product.wrapperTypes?.length ?? 0) > 0 && (
                    <>
                      <span className="text-muted-foreground">{t.wrapperTypes}:</span>
                      <span className="flex flex-wrap gap-1">
                        {/* Preference-ordered; the first is the routing preference */}
                        {product.wrapperTypes?.map((name, index) => (
                          <Badge key={name} variant={index === 0 ? "secondary" : "outline"}>
                            {name}
                          </Badge>
                        ))}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Costs */}
            {product.costs.length > 0 && (
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">{t.costs}</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {product.costs.map((cost) => (
                    <div key={cost.name} className="contents">
                      <span className="text-muted-foreground">{cost.name}:</span>
                      <span>
                        {cost.originalValue !== undefined ? (
                          <OverriddenCostValue
                            value={`${cost.value}%`}
                            original={`${cost.originalValue}%`}
                          />
                        ) : (
                          `${cost.value}%`
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Asset Allocation */}
            {product.assetAllocations.length > 0 && (
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  {t.assetAllocation}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {product.assetAllocations.map((alloc) => (
                    <div key={alloc.name} className="contents">
                      <span className="text-muted-foreground">{alloc.name}:</span>
                      <span>{alloc.weight}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            {product.documents.length > 0 && (
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">{t.documents}</div>
                <div className="flex flex-wrap gap-2">
                  {product.documents.map((doc) => (
                    <a
                      key={doc.name}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                    >
                      {doc.name}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4">{t.productDetailsNotAvailable}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PortfolioDisplayWidget({
  snapshot,
  showChart = false,
  showTable = true,
  chartColors,
  showPortfolioName = true,
  style = "none",
  title,
  description,
  language,
  portfolioId,
}: PortfolioDisplayWidgetProps) {
  // Public client-flow surface: the info modal must use the public details
  // endpoint (advisor sessionAuth would 401 for a public visitor).
  const session = usePublicSession();
  const { userLanguage } = useUserLanguage();
  const { formatNumber } = useRegionalFormat();
  const lang = language || userLanguage;
  const t = pickT(T, lang);

  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [productDetails, setProductDetails] = useState<ProductDetailsResponse | null>(null);
  const [isPending, startTransition] = useTransition();

  const handleOpenInfo = (productId: string) => {
    setSelectedProductId(productId);
    setProductDetails(null);
    startTransition(async () => {
      try {
        // Public visitors have no advisor session — the internal endpoint
        // would 401 and the modal would show "not available". Use the flow's
        // language on the public branch (userLanguage is advisor context).
        const details = session.isPublic
          ? await getPublicProductDetails(session.sessionToken, productId, lang)
          : portfolioId
            ? await getPortfolioProductDetails(portfolioId, productId)
            : await getProductDetails(productId);
        setProductDetails(details);
      } catch {
        setProductDetails(null);
      }
    });
  };

  const handleCloseInfo = () => {
    setSelectedProductId(null);
    setProductDetails(null);
  };

  // Get container class based on style
  const getContainerClass = () => {
    switch (style) {
      case "border":
        return "border rounded-lg p-6 space-y-4";
      case "box":
        return "rounded-lg p-6 space-y-4 bg-muted/50";
      default:
        return "space-y-4";
    }
  };

  const products = useMemo(
    () =>
      (snapshot.products || []).map((p, i) => ({
        key: p.productId ?? p.isin ?? `${p.productName}-${i}`,
        id: p.productId,
        name: p.productName,
        isin: p.isin,
        weight: p.weight,
        amount: p.amount,
      })),
    [snapshot.products],
  );
  const slices = useMemo(
    () => portfolioDisplaySlices(products, chartColors),
    [products, chartColors],
  );
  // A config with both off (saved before the dialogs refused it) keeps the table.
  const tableShown = showTable || !showChart;

  const portfolioName = snapshot.portfolioName;
  const timestamp = snapshot.timestamp;
  const currency = snapshot.currency;
  const totalAmount = snapshot.totalAmount;

  // Only show amounts if they're non-zero (hide for model portfolios with no investment amount)
  const hasAmounts = totalAmount && totalAmount > 0;

  return (
    <div className={`w-full ${getContainerClass()}`}>
      <FlowComponentHeader title={title} description={description} />

      {products.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t.noProducts}</p>
      ) : (
        <>
          {showChart && (
            <PortfolioDisplayChart
              slices={slices}
              showLegend={!tableShown}
              language={lang}
              onOpenInfo={handleOpenInfo}
              unmatchableTooltip={t.unmatchableNoDetails}
            />
          )}
          {tableShown && (
            <Table variant="data">
              <TableHeader>
                <TableRow>
                  {showChart && <TableHead className="w-8" />}
                  <TableHead>{t.productName}</TableHead>
                  <TableHead>{t.isin}</TableHead>
                  {hasAmounts && <TableHead className="text-right">{t.amount}</TableHead>}
                  <TableHead className="text-right">{t.weight}</TableHead>
                  <TableActionsHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product, i) => (
                  <TableRow key={product.key}>
                    {showChart && (
                      <TableCell>
                        <div
                          className="size-3 rounded-sm"
                          style={{ backgroundColor: slices[i].color }}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell className="font-mono text-sm">{product.isin}</TableCell>
                    {hasAmounts && product.amount !== undefined && (
                      <TableCell className="text-right whitespace-nowrap">
                        {formatNumber(product.amount)} {currency}
                      </TableCell>
                    )}
                    <TableCell className="text-right">{product.weight.toFixed(2)}%</TableCell>
                    <TableActionsCell>
                      {product.id ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => handleOpenInfo(product.id as string)}
                          tooltip={product.name}
                        >
                          <Info className="h-4 w-4" />
                        </Button>
                      ) : (
                        // Unmatchable holding (QL-304): no collection product, so no
                        // details to fetch. The span carries the hover — a disabled
                        // button has pointer-events-none, so its own tooltip cannot.
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground"
                                disabled
                                tooltip={t.unmatchableNoDetails}
                              >
                                <Info className="h-4 w-4" />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {t.unmatchableNoDetails}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </TableActionsCell>
                  </TableRow>
                ))}
              </TableBody>
              {hasAmounts && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={showChart ? 3 : 2} className="font-semibold">
                      {t.total}
                    </TableCell>
                    <TableCell className="text-right font-semibold whitespace-nowrap">
                      {formatNumber(totalAmount)} {currency}
                    </TableCell>
                    <TableCell className="text-right font-semibold">100%</TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          )}
        </>
      )}
      {(showPortfolioName && portfolioName) || timestamp ? (
        <div className="text-xs text-muted-foreground mt-4">
          {showPortfolioName && portfolioName && (
            <div>
              {t.portfolio}: {portfolioName}
            </div>
          )}
          {timestamp && (
            <div>
              {t.snapshotTaken} {getRelativeTime(timestamp, lang)}
            </div>
          )}
        </div>
      ) : null}

      <ProductInfoModal
        open={selectedProductId !== null}
        onClose={handleCloseInfo}
        product={productDetails}
        isLoading={isPending}
        t={t}
      />
    </div>
  );
}
